import React from 'react';
import { useTranslation } from '@shared/core';
import { EventType } from '@graphql/generated';
import { ReactComponent as GoogleIcon } from '@assets/icons/google.svg';
import { ReactComponent as FacebookIcon } from '@assets/icons/facebook.svg';
import { ButtonV2, FormControl, SpacingStack, TextV2 } from '@withjoy/joykit';
import { EventInputV2, EventPasswordInputV2 } from '@shared/createEvent/formElements';

import { StyledForm } from './CreateAccount.styles';
import { useCreateAccountController } from '@shared/createEvent/CreateAccount/CreateAccount.controller';
import { wrapWithCreateAccountHealthProvider } from '@shared/createEvent/CreateAccount/CreateAccount.health';
import { PrivacyPolicyLink, TermsOfServiceLink, PrivacyNoticeLink, ShowDevAlert, PixelImage } from '@shared/createEvent/CreateAccount/components';
import { getEventTypeTranslationWithGeneralFallback } from '@shared/utils/getEventTypeTranslationWithGeneralFallback';

export interface CreateAccountProps {
  appName: string;
  eventType: EventType;
}

export const CreateAccount: React.FC<CreateAccountProps> = wrapWithCreateAccountHealthProvider(props => {
  const { t } = useTranslation('createEvent');
  const { formik, submissionError, signInWithFacebook, signInWithGoogle, showSocialSignIn, focusHandlers, getFieldErrorProps } = useCreateAccountController(props);
  const translations = t('createAccount');
  const { eventType } = props;

  const createAccountTranslations = getEventTypeTranslationWithGeneralFallback(translations, eventType, EventType.wedding);
  const devAlertMessage = createAccountTranslations.devAlertMessage();

  const termsAndPrivacy = translations.termsAndPrivacy({ PrivacyPolicy: PrivacyPolicyLink, TermsOfService: TermsOfServiceLink, PrivacyNotice: PrivacyNoticeLink });

  return (
    <SpacingStack spacing={9}>
      <ShowDevAlert redirectPath={props.appName} message={devAlertMessage} />
      {showSocialSignIn && (
        <SpacingStack spacing={6} width={336} marginX="auto">
          <ButtonV2 onClick={signInWithFacebook} variant="outline" startIcon={<FacebookIcon />}>
            {translations.signInWithFacebook()}
          </ButtonV2>
          <ButtonV2 onClick={signInWithGoogle} variant="outline" startIcon={<GoogleIcon />}>
            {translations.signInWithGoogle()}
          </ButtonV2>
        </SpacingStack>
      )}
      <StyledForm onSubmit={formik.handleSubmit}>
        <SpacingStack spacing={9} width={['100%', 336]}>
          <SpacingStack spacing={6}>
            <FormControl
              {...getFieldErrorProps('email')}
              label={
                <TextV2 typographyVariant="hed1" marginBottom={4}>
                  {translations.email()}
                </TextV2>
              }
            >
              <EventInputV2 onFocus={focusHandlers.email} {...formik.getFieldProps('email')} {...getFieldErrorProps('email')} />
            </FormControl>
            <FormControl
              {...getFieldErrorProps('password')}
              label={
                <TextV2 typographyVariant="hed1" marginBottom={4}>
                  {translations.password()}
                </TextV2>
              }
            >
              <EventPasswordInputV2 onFocus={focusHandlers.password} {...formik.getFieldProps('password')} {...getFieldErrorProps('password')} />
            </FormControl>
            {submissionError && (
              <TextV2 textAlign="center" typographyVariant="body1" color="negative6">
                {submissionError}
              </TextV2>
            )}
          </SpacingStack>
          <SpacingStack spacing={9} paddingTop={3}>
            <ButtonV2 type="submit" disabled={formik.isSubmitting} intent="primary" marginX={6} borderRadius={64} height={10}>
              {translations.createAccountCta()}
            </ButtonV2>
            <SpacingStack>
              <TextV2 textAlign="center" typographyVariant="label1" color="mono10" style={{ lineHeight: '1.17rem' }}>
                {termsAndPrivacy}
                <PixelImage action="signup" />
              </TextV2>
            </SpacingStack>
          </SpacingStack>
        </SpacingStack>
      </StyledForm>
    </SpacingStack>
  );
});
